import React, { useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Dimensions } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../contexts/ThemeContext';
import Footer from '../components/Footer';
import { formatCurrency, formatNumber } from '../utils/formatters';

const { width } = Dimensions.get('window');

export default function MarginCalculatorScreen() {
  const { colors } = useTheme();
  const navigation = useNavigation();

  const [segment, setSegment] = useState('Intraday');
  const [quantity, setQuantity] = useState('100');
  const [price, setPrice] = useState('100');
  const [mode, setMode] = useState('Leverage'); // 'Leverage' | 'Margin%'
  const [leverage, setLeverage] = useState('10');
  const [marginPercent, setMarginPercent] = useState('20');

  const num = (v, d = 0) => {
    const n = parseFloat(String(v).replace(/,/g, ''));
    if (isNaN(n)) return d;
    return n;
  };

  const data = useMemo(() => {
    const qty = Math.max(0, num(quantity, 0));
    const p = Math.max(0, num(price, 0));
    const lev = Math.max(1, num(leverage, 1));
    const mp = Math.max(0, num(marginPercent, 0));
    const exposure = qty * p;
    const requiredMargin = mode === 'Leverage' ? (exposure / lev) : (exposure * mp / 100);
    const onePercentMovePnL = exposure * 0.01;
    const oneRupeeMovePnL = qty * 1; // per ₹1 move
    return { exposure, requiredMargin, onePercentMovePnL, oneRupeeMovePnL };
  }, [quantity, price, leverage, marginPercent, mode]);

  const resetAll = () => {
    setSegment('Intraday');
    setQuantity('100');
    setPrice('100');
    setMode('Leverage');
    setLeverage('10');
    setMarginPercent('20');
  };

  const currency = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <View style={styles.heroSection}>
        <View style={styles.heroContent}>
          <Text style={styles.heroTitle}>Margin Calculator</Text>
          <Text style={styles.heroSubtitle}>Estimate required funds and risk snapshot</Text>
        </View>
      </View>

      <View style={styles.calculatorContainer}>
        <View style={styles.calculatorRow}>
          <View style={styles.leftPanel}>
            <View style={styles.card}>
              <Text style={styles.sectionTitle}>Trade Inputs</Text>
              <View style={styles.row}>
                <TouchableOpacity style={[styles.toggle, segment === 'Delivery' && styles.toggleActive]} onPress={() => setSegment('Delivery')}>
                  <Text style={[styles.toggleText, segment === 'Delivery' && styles.toggleTextActive]}>Delivery</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.toggle, segment === 'Intraday' && styles.toggleActive]} onPress={() => setSegment('Intraday')}>
                  <Text style={[styles.toggleText, segment === 'Intraday' && styles.toggleTextActive]}>Intraday</Text>
                </TouchableOpacity>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Quantity</Text>
                <TextInput value={quantity} onChangeText={setQuantity} style={styles.input} keyboardType="numeric" placeholder="e.g. 100" />
              </View>

              <View style={styles.row}> 
                <View style={[styles.inputGroup, styles.flexItem]}> 
                  <Text style={styles.label}>Price (₹)</Text>
                  <TextInput value={price} onChangeText={setPrice} style={styles.input} keyboardType="numeric" placeholder="e.g. 100" />
                </View>
              </View>
            </View>

            <View style={styles.card}>
              <Text style={styles.sectionTitle}>Margin Settings</Text>
              <View style={styles.row}>
                <TouchableOpacity style={[styles.toggle, mode === 'Leverage' && styles.toggleActive]} onPress={() => setMode('Leverage')}>
                  <Text style={[styles.toggleText, mode === 'Leverage' && styles.toggleTextActive]}>Leverage (×)</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[styles.toggle, mode === 'Margin%' && styles.toggleActive]} onPress={() => setMode('Margin%')}>
                  <Text style={[styles.toggleText, mode === 'Margin%' && styles.toggleTextActive]}>Margin (%)</Text>
                </TouchableOpacity>
              </View>

              {mode === 'Leverage' ? (
                <View style={styles.inputGroup}>
                  <Text style={styles.label}>Leverage (×)</Text>
                  <TextInput value={leverage} onChangeText={setLeverage} style={styles.input} keyboardType="numeric" placeholder="e.g. 10" />
                </View>
              ) : (
                <View style={styles.inputGroup}>
                  <Text style={styles.label}>Margin Required (%)</Text>
                  <TextInput value={marginPercent} onChangeText={setMarginPercent} style={styles.input} keyboardType="numeric" placeholder="e.g. 20" />
                </View>
              )}
            </View>
          </View>

          <View style={styles.rightPanel}>
            <View style={styles.card}>
              <View style={styles.panelHeader}>
                <Text style={styles.panelTitle}>Margin Requirements</Text>
                <Text style={styles.panelSubtitle}>Exposure and minimum funds</Text>
              </View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Exposure</Text><Text style={styles.summaryValue}>{currency.format(data.exposure)}</Text></View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Required Margin</Text><Text style={styles.summaryValue}>{currency.format(data.requiredMargin)}</Text></View>
            </View>

            <View style={styles.card}>
              <View style={styles.panelHeader}>
                <Text style={styles.panelTitle}>Risk Snapshot</Text>
                <Text style={styles.panelSubtitle}>Estimated PnL for price moves</Text>
              </View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>PnL per ₹1 move</Text><Text style={styles.summaryValue}>{currency.format(data.oneRupeeMovePnL)}</Text></View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>PnL per 1% move</Text><Text style={styles.summaryValue}>{currency.format(data.onePercentMovePnL)}</Text></View>
              <View style={{ marginTop: 12, flexDirection: 'row', gap: 8 }}>
                <TouchableOpacity style={styles.buttonPrimary} onPress={resetAll}><Text style={styles.buttonText}>Reset</Text></TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </View>

      {renderSEO(navigation)}
      {renderFAQ()}
      <Footer />
    </ScrollView>
  );
}

function renderSEO(navigation) {
  return (
    <View style={styles.seoSection}>
      <Text style={styles.seoTitle}>About Margin Calculator</Text>
      <Text style={styles.seoText}>
        Margin is the minimum capital required to open or hold a position. This tool shows exposure, funds required based on leverage or margin percentage, and quick PnL snapshots for price movements.
      </Text>

      <Text style={styles.seoSubtitle}>Leverage vs. Margin %</Text>
      <Text style={styles.seoText}>
        With leverage, exposure is funds × leverage. Required margin = exposure ÷ leverage. With margin percentage, required margin = exposure × margin%. Both models are approximations and vary by broker and product.
      </Text>

      <Text style={styles.seoSubtitle}>Risk tips</Text>
      <Text style={styles.seoText}>
        Account for gaps, slippage, and charges. Tight risk controls and position sizing help manage volatility. Consider worst-case scenarios before using high leverage.
      </Text>

      <View style={styles.linksRow}>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('BrokerageCalculator')}>
          <Text style={styles.linkText}>Brokerage Calculator</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('CAGRCalculator')}>
          <Text style={styles.linkText}>CAGR Calculator</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('XIRRCalculator')}>
          <Text style={styles.linkText}>XIRR Calculator</Text>
        </TouchableOpacity>
      </View>

      <Text style={styles.disclaimer}>
        Actual margins depend on product, broker, exchange rules, and risk policies. Use this estimate as a guide only and confirm with your broker before trading.
      </Text>
    </View>
  );
}

function renderFAQ() {
  return (
    <View style={styles.faqContainer}>
      <Text style={styles.faqTitle}>Frequently Asked Questions</Text>

      <View style={styles.faqItem}>
        <Text style={styles.faqQuestion}>How is required margin calculated?</Text>
        <Text style={styles.faqAnswer}>Required margin ≈ exposure ÷ leverage, or exposure × margin% depending on product and broker rules.</Text>
      </View>

      <View style={styles.faqItem}>
        <Text style={styles.faqQuestion}>Does higher leverage reduce risk?</Text>
        <Text style={styles.faqAnswer}>No. Higher leverage reduces capital needed but increases position sensitivity to price moves and potential losses.</Text>
      </View>

      <View style={styles.faqItem}>
        <Text style={styles.faqQuestion}>Are brokerage and taxes included?</Text>
        <Text style={styles.faqAnswer}>This calculator focuses on margin and exposure. Use the Brokerage Calculator to estimate charges and net PnL.</Text>
      </View>

      <View style={styles.faqItem}>
        <Text style={styles.faqQuestion}>What happens if price gaps against me?</Text>
        <Text style={styles.faqAnswer}>Gaps can exceed stop levels, leading to larger-than-expected losses. Plan for adverse scenarios.</Text>
      </View>

      <View style={styles.faqItem}>
        <Text style={styles.faqQuestion}>Delivery vs Intraday margin?</Text>
        <Text style={styles.faqAnswer}>Intraday products often have lower margins but must be squared off the same day. Delivery margins are typically higher and vary by exchange and product.</Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8fafc' },
  content: { paddingBottom: 24 },
  heroSection: { backgroundColor: '#4F46E5', paddingVertical: 18, paddingHorizontal: 16 },
  heroContent: { maxWidth: 1200, alignSelf: 'center' },
  heroTitle: { fontSize: 22, fontWeight: '800', color: '#ffffff', marginBottom: 4 },
  heroSubtitle: { fontSize: 14, color: '#e0e7ff' },
  calculatorContainer: { width: '100%', alignSelf: 'stretch', paddingHorizontal: 16, paddingVertical: 12 },
  calculatorRow: { flexDirection: 'row', gap: 24 },
  leftPanel: { flex: 0.6 },
  rightPanel: { flex: 0.4 },
  card: { backgroundColor: '#ffffff', borderRadius: 12, padding: 16, marginTop: 16, marginHorizontal: 16, borderWidth: 1, borderColor: '#e5e7eb' },
  sectionTitle: { fontSize: 18, fontWeight: '700', color: '#0f172a', marginBottom: 12, marginHorizontal: 16 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  inputGroup: { marginVertical: 6 },
  input: { backgroundColor: '#f1f5f9', borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10 },
  label: { fontSize: 13, color: '#334155', marginBottom: 6 },
  flexItem: { flex: 1 },
  toggle: { paddingVertical: 8, paddingHorizontal: 12, borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 8, backgroundColor: '#f1f5f9' },
  toggleActive: { backgroundColor: '#eef2ff', borderColor: '#c7d2fe' },
  toggleText: { color: '#334155', fontSize: 13 },
  toggleTextActive: { color: '#1e293b', fontWeight: '600' },
  panelHeader: { alignItems: 'center', marginBottom: 10, paddingBottom: 8, borderBottomWidth: 1, borderBottomColor: '#e5e7eb' },
  panelTitle: { fontSize: 18, fontWeight: '600', color: '#0f172a' },
  panelSubtitle: { fontSize: 12, color: '#64748b', marginTop: 2 },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 6 },
  summaryLabel: { color: '#334155' },
  summaryValue: { color: '#0f172a', fontWeight: '600' },
  buttonPrimary: { backgroundColor: '#4F46E5', paddingVertical: 10, paddingHorizontal: 14, borderRadius: 8 },
  buttonText: { color: '#ffffff', fontWeight: '600' },
  seoSection: { backgroundColor: '#ffffff', borderRadius: 12, padding: 16, marginTop: 16, borderWidth: 1, borderColor: '#e5e7eb', marginHorizontal: 16 },
  seoTitle: { fontSize: 18, fontWeight: '700', color: '#0f172a', marginBottom: 8 },
  seoSubtitle: { fontSize: 16, fontWeight: '600', color: '#0f172a', marginTop: 12, marginBottom: 6 },
  seoText: { color: '#334155', lineHeight: 20 },
  linksRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 10 },
  linkButton: { backgroundColor: '#EEF2FF', borderColor: '#c7d2fe', borderWidth: 1, borderRadius: 8, paddingVertical: 8, paddingHorizontal: 12 },
  linkText: { color: '#1e293b', fontWeight: '600' },
  disclaimer: { color: '#64748b', fontSize: 12, marginTop: 10 },
  faqContainer: { backgroundColor: '#ffffff', borderRadius: 12, padding: 16, marginTop: 16, borderWidth: 1, borderColor: '#e5e7eb', marginHorizontal: 16 },
  faqTitle: { fontSize: 18, fontWeight: '700', color: '#0f172a', marginBottom: 8 },
  faqItem: { marginBottom: 10 },
  faqQuestion: { fontSize: 15, fontWeight: '600', color: '#0f172a' },
  faqAnswer: { color: '#334155', marginTop: 4, lineHeight: 20 },
});